import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Download, Smartphone, Monitor } from "lucide-react";
import { InnerShadowTopIcon } from "@/utilities/icon-utils";
import { usePWAInstall } from "@/hooks/use-pwa-install";
import { SHOW_PWA_INSTALL_UI } from "@/pwa/pwa-install-flags";
import { cn, layout, typography } from "@/utilities/tailwind-utils";

export function PWAInstallPrompt() {
  const [isVisible, setIsVisible] = useState(false);
  const {
    canInstall,
    isInstalled,
    isInstalling,
    showPrompt,
    showInstallPrompt,
    hideInstallPrompt,
    handleInstall
  } = usePWAInstall();

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    // Wait for animation to complete before hiding
    setTimeout(() => hideInstallPrompt(), 300);
  }, [hideInstallPrompt]);

  useEffect(() => {
    if (!SHOW_PWA_INSTALL_UI) {
      return;
    }
    // Show prompt after a delay if it can be installed
    if (canInstall && !isInstalled) {
      const showTimer = setTimeout(() => {
        showInstallPrompt();
        // Add a small delay for smooth animation
        setTimeout(() => setIsVisible(true), 100);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(showTimer);
    }
  }, [canInstall, isInstalled, showInstallPrompt]);

  // Auto-hide timeout effect
  useEffect(() => {
    let autoHideTimer: NodeJS.Timeout | null = null;

    if (showPrompt && isVisible && !isInstalling) {
      // Set auto-hide timer for 10 seconds after the prompt becomes visible
      autoHideTimer = setTimeout(() => {
        handleDismiss();
      }, 10000); // Auto-hide after 10 seconds
    }

    return () => {
      if (autoHideTimer) {
        clearTimeout(autoHideTimer);
      }
    };
  }, [showPrompt, isVisible, isInstalling, handleDismiss]);

  if (!SHOW_PWA_INSTALL_UI || isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-4 left-4 right-4 sm:left-4 sm:right-auto z-50 transition-all duration-300 ease-in-out",
        isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
      )}
    >
      <Card
        className={cn(
          "relative overflow-hidden border-0 shadow-xl",
          "bg-white/80 dark:bg-neutral-900/80",
          "border border-neutral-200/60 dark:border-neutral-800/60",
          "backdrop-blur-lg w-full sm:w-96 max-w-sm mx-auto"
        )}
        style={{ backdropFilter: "blur(16px)" }}
      >
        {/* Animated background indicator */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 to-emerald-500/5 animate-pulse pointer-events-none" />

        <div className={cn("relative p-4", layout.flexCenterGap3)}>
          {/* Status icon with background */}
          <div
            className={cn(
              "flex-shrink-0 w-10 h-10 rounded-full",
              "bg-green-100 dark:bg-green-900/50",
              "flex items-center justify-center"
            )}
          >
            {isInstalling ? (
              <Download className="h-5 w-5 text-green-600 dark:text-green-400 animate-bounce" />
            ) : (
              <InnerShadowTopIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            )}
          </div>

          {/* Content */}
          <div className={cn("flex-1 min-w-0", layout.flexColGap1)}>
            <div className={cn(layout.flexCenterGap2)}>
              <h4
                className={cn(
                  typography.fontSemibold,
                  "text-green-900 dark:text-green-100"
                )}
              >
                {isInstalling ? "Installing..." : "Install Tempii"}
              </h4>
              <Badge
                variant="outline"
                className="text-green-700 border-green-300 dark:text-green-300 dark:border-green-700"
              >
                {isInstalling ? "Installing" : "PWA"}
              </Badge>
            </div>
            <p
              className={cn(
                typography.textSm,
                "text-green-700 dark:text-green-200/80"
              )}
            >
              {isInstalling
                ? "Installing Tempii on your device..."
                : "Install Tempii for a better experience with offline access and quick access."}
            </p>

            {/* Platform icons */}
            <div
              className={cn(
                "flex items-center gap-2 mt-2",
                typography.textXs,
                "text-green-600 dark:text-green-300"
              )}
            >
              <Monitor className="h-3 w-3" />
              <span>Desktop</span>
              <Smartphone className="h-3 w-3" />
              <span>Mobile</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className={cn("flex-shrink-0", layout.flexCenterGap2)}>
            {!isInstalling && (
              <Button
                size="sm"
                onClick={() => {
                  handleInstall();
                }}
                disabled={isInstalling}
                className={cn(
                  "bg-green-600 hover:bg-green-700",
                  "text-white shadow-sm",
                  "transition-all duration-200",
                  "hover:shadow-md active:scale-95"
                )}
              >
                Install
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              disabled={isInstalling}
              className={cn(
                "flex-shrink-0 h-8 w-8 p-0 rounded-full",
                "text-green-600 hover:text-green-700",
                "hover:bg-green-100 dark:hover:bg-green-900/50",
                "transition-colors duration-200"
              )}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
